import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaCalendarAlt } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import Modal from '../../../../components/Modal/Modal';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { Container } from '../../../../styles/layout/Container';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import {
   EntityActionBar,
   ActionBarSpacer,
} from '../../../../styles/ui/ActionBar';
import {
   CtaButton,
   DangerButton,
   GhostButton,
   SmallButton,
} from '../../../../styles/ui/Button';
import {
   getCandidate,
   deleteCandidate,
} from '../../../../services/hr/recruitment/candidatesApi';
import { getCandidateOnboarding } from '../../../../services/hr/recruitment/onboardingApi';
import { listUserDirectory } from '../../../../services/users/usersApi';
import { formatUserLabel } from '../../../../utils/format';

import StageBadge from '../components/StageBadge';
import FinalOutcomeBadge from '../components/FinalOutcomeBadge';
import JobOpeningStatusBadge from '../components/JobOpeningStatusBadge';
import PullToOpeningModal from '../components/PullToOpeningModal';
import {
   STAGE,
   roundForStage,
   isTerminalStage,
} from '../constants';
import {
   formatCpf,
   formatPhoneBr,
   formatDateBr,
   initialsOf,
} from '../utils/format';
import { recruitmentErrorMessage } from '../utils/apiError';

import ResumeFact from './components/ResumeFact';
import OnboardingCard from './components/OnboardingCard';
import InterviewTimeline from './components/InterviewTimeline';
import ScheduleInterviewModal from './components/ScheduleInterviewModal';
import InterviewResultModal from './components/InterviewResultModal';
import RescheduleInterviewModal from './components/RescheduleInterviewModal';
import EditInterviewModal from './components/EditInterviewModal';
import {
   HeaderBar,
   HeaderMain,
   Avatar,
   TitleRow,
   MetaLine,
   Card,
   CardHead,
   Fact,
   InlineHint,
   BackRow,
   HeadFact,
   IdentityGrid,
   ObsBlock,
   Split,
} from './styles';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Ficha do candidato (handoff 0061 §5.2). Dados + idade
// (derivada, read-only) + currículo (upload/download) + esteira de entrevistas.
// `stage`/`final_outcome` são exibidos, nunca editados: a UI dispara AÇÕES
// (puxar pra vaga, resultado, remarcar) e o backend muda o estado. Em stage
// terminal (ONBOARDING/ADMITIDO/ENCERRADO) a tela é só-exibição.
// ---------------------------------------------------------------------------

// Ordena as entrevistas por created_at ascendente (o histórico lê de cima pra
// baixo). Defensivo caso o backend não garanta a ordem.
function sortInterviews(interviews) {
   return [...interviews].sort((a, b) => {
      const at = new Date(a.created_at ?? 0).getTime();
      const bt = new Date(b.created_at ?? 0).getTime();
      return at - bt;
   });
}

export default function CandidateShow() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { hasPermission } = usePermissions();

   const canUpdate = hasPermission('hr:candidate:update');
   const canDelete = hasPermission('hr:candidate:delete');
   const canManageInterviews =
      hasPermission('hr:candidate_interview:create') ||
      hasPermission('hr:candidate_interview:update');

   const [candidate, setCandidate] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [notFound, setNotFound] = useState(false);
   // id → nome de entrevistador, para exibir no card sem depender de o backend
   // ter populado o objeto `interviewer`.
   const [interviewerNameById, setInterviewerNameById] = useState({});
   // Onboarding do candidato (F1) — null enquanto não existe; decide o layout
   // em Split (card de onboarding + esteira lado a lado).
   const [onboarding, setOnboarding] = useState(null);

   // Modais.
   const [pullOpen, setPullOpen] = useState(false);
   const [scheduleOpen, setScheduleOpen] = useState(false);
   const [resultTarget, setResultTarget] = useState(null);
   const [rescheduleTarget, setRescheduleTarget] = useState(null);
   const [editTarget, setEditTarget] = useState(null);
   const [deleteOpen, setDeleteOpen] = useState(false);
   const [isDeleting, setIsDeleting] = useState(false);

   const fetchCandidate = useCallback(async () => {
      setIsLoading(true);
      try {
         const result = await getCandidate(id);
         if (!result) setNotFound(true);
         else setCandidate(result);
      } catch (err) {
         if (err?.response?.status === 404) {
            setNotFound(true);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao carregar o candidato.'),
            );
            setNotFound(true);
         }
      } finally {
         setIsLoading(false);
      }
   }, [id]);

   useEffect(() => {
      fetchCandidate();
   }, [fetchCandidate]);

   // Onboarding (F1): busca gateada por permissão + stage compatível —
   // ONBOARDING/ADMITIDO, ou ENCERRADO com outcome DESQUALIFICADO (candidato
   // desqualificado DURANTE o onboarding ainda tem onboarding acessível).
   // Evita GET/404 nos encerrados comuns de entrevista. 404 → null.
   const canReadOnboarding = hasPermission('hr:candidate_onboarding:read');
   const candidateStage = candidate?.stage;
   const candidateOutcome = candidate?.final_outcome;

   useEffect(() => {
      const wantsFetch =
         canReadOnboarding &&
         (candidateStage === STAGE.ONBOARDING ||
            candidateStage === STAGE.ADMITIDO ||
            (candidateStage === STAGE.ENCERRADO &&
               candidateOutcome === 'DESQUALIFICADO'));

      if (!wantsFetch) {
         setOnboarding(null);
         return undefined;
      }
      let ignore = false;
      getCandidateOnboarding(id)
         .then((result) => {
            if (!ignore) setOnboarding(result);
         })
         .catch(() => {
            /* resumo opcional: sem onboarding legível, o card não aparece */
         });
      return () => {
         ignore = true;
      };
   }, [canReadOnboarding, candidateStage, candidateOutcome, id]);

   // Resolve nomes de entrevistadores (id → nome) só quando o backend não trouxe
   // o objeto `interviewer` populado — mesma fonte do seletor (GET /users).
   useEffect(() => {
      const interviews = candidate?.interviews ?? [];
      const needsResolve = interviews.some(
         (i) => i.interviewer_user_id && !i.interviewer?.name,
      );
      if (!needsResolve) return undefined;

      let ignore = false;
      // `GET /users/directory?status=all` (handoff 0110 §3.9): resolução de
      // nome precisa alcançar quem já saiu — entrevista realizada não perde o
      // entrevistador porque a conta dele foi excluída depois.
      listUserDirectory({ status: 'all' })
         .then((list) => {
            if (ignore) return;
            const map = {};
            list.forEach((user) => {
               map[String(user.id)] = formatUserLabel(user);
            });
            setInterviewerNameById(map);
         })
         .catch(() => {
            /* enriquecimento opcional: sem o nome, o card omite o entrevistador */
         });
      return () => {
         ignore = true;
      };
   }, [candidate]);

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/hr/recruitment/pool');
   };

   const handleConfirmDelete = async () => {
      if (isDeleting) return;
      setIsDeleting(true);
      try {
         await deleteCandidate(id);
         toast.success('Candidato excluído.');
         navigate('/hr/recruitment/pool');
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(
               err,
               'Não foi possível excluir o candidato.',
            ),
         );
         setDeleteOpen(false);
      } finally {
         setIsDeleting(false);
      }
   };

   if (isLoading && !candidate) {
      return (
         <Container>
            <Loading isLoading />
         </Container>
      );
   }

   if (notFound || !candidate) {
      return (
         <Container>
            <BackRow>
               <GhostButton type="button" onClick={handleBack}>
                  <FaArrowLeft /> Voltar
               </GhostButton>
            </BackRow>
            <EmptyState
               title="Candidato não encontrado"
               description="Este candidato não existe ou não pôde ser carregado."
            />
         </Container>
      );
   }

   const stage = candidate.stage;
   const isPool = stage === STAGE.BANCO_CURRICULOS;
   const isTerminal = isTerminalStage(stage);
   const round = roundForStage(stage);

   const interviews = sortInterviews(candidate.interviews ?? []);
   const activeInterview = round
      ? interviews
           .filter((i) => i.round === round && !i.result)
           .slice(-1)[0] ?? null
      : null;
   const needsSchedule = Boolean(round) && !activeInterview;

   const jobOpening = candidate.job_opening;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <BackRow>
            <GhostButton type="button" onClick={handleBack}>
               <FaArrowLeft /> Voltar
            </GhostButton>
         </BackRow>

         <HeaderBar>
            <HeaderMain>
               <Avatar>{initialsOf(candidate.full_name)}</Avatar>
               <div>
                  <TitleRow>
                     <h1>{candidate.full_name}</h1>
                     <StageBadge value={stage} />
                     {isTerminal && (
                        <FinalOutcomeBadge value={candidate.final_outcome} />
                     )}
                  </TitleRow>
                  <MetaLine>
                     <span>
                        {candidate.desired_position?.position_name ??
                           'Cargo não informado'}
                     </span>
                     <span className="dot">·</span>
                     <span>
                        Origem: {candidate.origin?.name ?? '—'}
                     </span>
                     {jobOpening && (
                        <>
                           <span className="dot">·</span>
                           <span>Vaga: {jobOpening.title ?? '—'}</span>
                        </>
                     )}
                  </MetaLine>
               </div>
            </HeaderMain>

            <EntityActionBar>
               {canDelete && (
                  <>
                     <DangerButton
                        type="button"
                        onClick={() => setDeleteOpen(true)}
                     >
                        Excluir
                     </DangerButton>
                     <ActionBarSpacer />
                  </>
               )}
               {canUpdate && isPool && (
                  <CtaButton type="button" onClick={() => setPullOpen(true)}>
                     Puxar pra vaga
                  </CtaButton>
               )}
               {canUpdate && (
                  <CtaButton
                     type="button"
                     onClick={() =>
                        navigate(
                           `/hr/recruitment/candidates/${candidate.id}/edit`,
                        )
                     }
                  >
                     Editar
                  </CtaButton>
               )}
            </EntityActionBar>
         </HeaderBar>

         {/* ─── Identidade & contato ────────────────────────────────────── */}
         <Card>
            <CardHead>
               <h2>Identidade &amp; contato</h2>
               {jobOpening && (
                  <HeadFact>
                     <span className="head-fact-label">Vaga atual</span>
                     <strong>{jobOpening.title ?? '—'}</strong>
                     {jobOpening.status && (
                        <JobOpeningStatusBadge value={jobOpening.status} />
                     )}
                  </HeadFact>
               )}
            </CardHead>
            <IdentityGrid>
               <Fact>
                  <div className="fact-label">Idade</div>
                  <div className="fact-value">
                     {candidate.age != null ? `${candidate.age} anos` : '—'}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">Nascimento</div>
                  <div className="fact-value muted">
                     {formatDateBr(candidate.birth_date)}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">CPF</div>
                  <div className="fact-value muted">
                     {formatCpf(candidate.cpf)}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">Telefone</div>
                  <div className="fact-value muted">
                     {formatPhoneBr(candidate.phone)}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">E-mail</div>
                  <div className="fact-value muted">
                     {candidate.email ?? '—'}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">Cargo pretendido</div>
                  <div className="fact-value">
                     {candidate.desired_position?.position_name ?? '—'}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">Origem</div>
                  <div className="fact-value">
                     {candidate.origin?.name ?? '—'}
                  </div>
               </Fact>
               <ResumeFact
                  candidateId={candidate.id}
                  hasResume={Boolean(candidate.has_resume)}
                  resumeOriginalName={candidate.resume_original_name}
                  onUploaded={fetchCandidate}
               />
            </IdentityGrid>

            {candidate.general_notes && (
               <ObsBlock>
                  <div className="obs-label">Observações</div>
                  <div className="obs-value">{candidate.general_notes}</div>
               </ObsBlock>
            )}
         </Card>

         {/* ─── Onboarding × Esteira (Split quando o onboarding existe) ── */}
         {(() => {
            const interviewsCard = (
               <Card>
                  <CardHead>
                     <h2>Esteira de entrevistas</h2>
                     {!isTerminal && canManageInterviews && needsSchedule && (
                        <SmallButton
                           type="button"
                           $intent="write"
                           onClick={() => setScheduleOpen(true)}
                        >
                           <FaCalendarAlt size={12} /> Agendar entrevista
                        </SmallButton>
                     )}
                  </CardHead>

                  {isPool && (
                     <InlineHint style={{ marginBottom: 12 }}>
                        Puxe o candidato para uma vaga aberta para iniciar a
                        esteira de entrevistas.
                     </InlineHint>
                  )}

                  <InterviewTimeline
                     interviews={interviews}
                     activeInterviewId={activeInterview?.id ?? null}
                     interviewerNameById={interviewerNameById}
                     showActions={!isTerminal}
                     canManage={canManageInterviews}
                     candidateStage={stage}
                     onResult={(interview) => setResultTarget(interview)}
                     onReschedule={(interview) => setRescheduleTarget(interview)}
                     onEdit={(interview) => setEditTarget(interview)}
                     onOpenOnboarding={() =>
                        navigate(
                           `/hr/recruitment/candidates/${candidate.id}/onboarding`,
                        )
                     }
                  />
               </Card>
            );

            if (!onboarding) return interviewsCard;
            return (
               <Split>
                  <OnboardingCard
                     candidateId={candidate.id}
                     onboarding={onboarding}
                  />
                  {interviewsCard}
               </Split>
            );
         })()}

         {/* ─── Modais ──────────────────────────────────────────────────── */}
         <PullToOpeningModal
            isOpen={pullOpen}
            onClose={() => setPullOpen(false)}
            candidate={candidate}
            onSaved={fetchCandidate}
         />

         {round && (
            <ScheduleInterviewModal
               isOpen={scheduleOpen}
               onClose={() => setScheduleOpen(false)}
               candidateId={candidate.id}
               round={round}
               onSaved={fetchCandidate}
            />
         )}

         <InterviewResultModal
            isOpen={Boolean(resultTarget)}
            onClose={() => setResultTarget(null)}
            interview={resultTarget}
            onSaved={fetchCandidate}
         />

         <RescheduleInterviewModal
            isOpen={Boolean(rescheduleTarget)}
            onClose={() => setRescheduleTarget(null)}
            interview={rescheduleTarget}
            onSaved={fetchCandidate}
         />

         <EditInterviewModal
            isOpen={Boolean(editTarget)}
            onClose={() => setEditTarget(null)}
            interview={editTarget}
            onSaved={fetchCandidate}
         />

         <Modal
            isOpen={deleteOpen}
            title="Excluir candidato"
            onClose={() => setDeleteOpen(false)}
            maxWidth="440px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setDeleteOpen(false)}
                     disabled={isDeleting}
                  >
                     Cancelar
                  </GhostButton>
                  <DangerButton
                     type="button"
                     onClick={handleConfirmDelete}
                     disabled={isDeleting}
                  >
                     {isDeleting ? 'Excluindo…' : 'Excluir candidato'}
                  </DangerButton>
               </>
            }
         >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
               Tem certeza que deseja excluir{' '}
               <strong>{candidate.full_name}</strong>? Candidatos com entrevistas
               registradas não podem ser excluídos.
            </p>
         </Modal>
      </Container>
   );
}
